import { createContext, useState, useEffect } from 'react'
import dayjs from 'dayjs'
import enGB from 'antd/locale/en_GB'
import ukUA from 'antd/locale/uk_UA'
import 'dayjs/locale/en-gb'
import 'dayjs/locale/uk'
import i18n from './i18n.js'
import { getNormalizedProfileSettings, normalizeUserSettings } from './utils/auth.js'

const ensureWeekStart = (antdLocale) => {
  const calendar = antdLocale?.Calendar || {}
  const datePicker = antdLocale?.DatePicker || {}
  const calendarLang = calendar.lang || {}
  const datePickerLang = datePicker.lang || calendarLang
  const weekStartValue = typeof calendarLang.weekStart === 'number' ? calendarLang.weekStart : 1

  return {
    ...antdLocale,
    Calendar: {
      ...calendar,
      lang: {
        ...calendarLang,
        weekStart: weekStartValue,
      },
    },
    DatePicker: {
      ...datePicker,
      lang: {
        ...datePickerLang,
        weekStart: typeof datePickerLang.weekStart === 'number' ? datePickerLang.weekStart : weekStartValue,
      },
    },
  }
}

const LOCALE_CONFIG = {
  en: {
    antd: ensureWeekStart(enGB),
    dayjs: 'en-gb',
  },
  uk: {
    antd: ensureWeekStart(ukUA),
    dayjs: 'uk',
  },
}

const getLocaleConfig = (language) => LOCALE_CONFIG[language] || LOCALE_CONFIG.en

export const LanguageContext = createContext({
  lang: 'en',
  antLocale: getLocaleConfig('en').antd,
  changeLanguage: () => {},
})

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const { lang: profileLang } = getNormalizedProfileSettings()
    if (profileLang) return profileLang
    const stored = normalizeUserSettings({ language: localStorage.getItem('lang') }).lang
    return stored || 'en'
  })

  const [antLocale, setAntLocale] = useState(() => getLocaleConfig(lang).antd)

  useEffect(() => {
    i18n.changeLanguage(lang)
    localStorage.setItem('lang', lang)
    const { antd, dayjs: dayjsLocale } = getLocaleConfig(lang)
    setAntLocale(antd)
    dayjs.locale(dayjsLocale)
  }, [lang])

  return (
    <LanguageContext.Provider value={{ lang, antLocale, changeLanguage: setLang }}>
      {children}
    </LanguageContext.Provider>
  )
}
